import { Entity, parseEntityRef } from '@backstage/catalog-model';
import { EmptyState } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  EntityRefLink,
} from '@backstage/plugin-catalog-react';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import LinearProgress from '@mui/material/LinearProgress';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useContributingProducts } from '../../hooks/useContributingProducts';
import { hierachyProps } from '../../redux/slices/hierachySlice';
import { getSystemByByName } from '../CatalogTable/CatalogTable';

/** @public */
export type ProductListProps = {
  title: string;
  type?: 'individual' | 'team';
  productData?: Omit<hierachyProps, 'loading' | 'error'>;
  classes?:any;
};



export function ProductList(props: ProductListProps) {
  
  const { title, productData,classes,type} = props;
  const [entities, setEntities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { list } = useSelector((state: any) => state.entraIdGroups);
  const { response } = useSelector((state: any) => state.hierachyList);
  const catalogApi = useApi(catalogApiRef);
  const { contributingProductsTeam } = useContributingProducts({
    productData,
    entraIdPeopleAppList: list,
    hierarchyList: response,
    contributingType: 'with ProductData',
  });  
  const fetchNamesByGroupId = useCallback(
    async (ids: string) => {
      if (!ids) return '';

      const split = ids.split(' ');
      if (split.length === 0) return '';

      try {
        const names = await Promise.all(
          split.map(async item => {
            const groupItem = { kind: 'group', name: item };
            const entityRef = await parseEntityRef(groupItem);
            const value = (await catalogApi.getEntityByRef(entityRef)) as any;
            return value?.spec?.profile?.displayName || 'Unknown';
          }),
        );
        return names.join(', ');
      } catch (error) {
        console.error('Error fetching group names:', error);
        return 'Error';
      }
    },
    [catalogApi],
  );

  // Memoized fetch for entity data, fetching in parallel
  const fetchEntities = useCallback(async () => {
    try {
      setLoading(true);
      const groupItem = window?.location.pathname.split('/')[4];
      const entityData = await getSystemByByName(groupItem);
      // Fetch owner names in parallel
      const processedEntities = await Promise.all(
        entityData.map(async (entity: any) => {
          const ownerNames = entity.spec?.owner.includes(' ')
            ? await fetchNamesByGroupId(entity.spec?.owner)
            : await fetchNamesByGroupId(entity.spec?.owner);
          return {
            ...entity,
            spec: {
              ...entity?.spec,
              link: ownerNames,
            },
          };
        }),
      );
      setEntities(processedEntities);
    } catch (err) {
      setError('Failed to load entities');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [fetchNamesByGroupId]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);


  return (
    <Card data-testid="entity-has-systems-card">
      <div style={{ display: 'flex' }}>
        <CardHeader title={title} style={{ marginBottom: '17px' }} />
        {type==='team'&&<span style={{ marginTop: '15px' }}>
        <Tooltip title={
          'These should be products that the teams within the VS are assigned to contributing teams'
        } placement="top"  arrow >
          <IconButton size="small">
          <button className={`${classes}`} >
            ?
          </button>
          </IconButton>
        </Tooltip>
        </span>}
      </div>
      <Divider />
      <CardContent>
        {loading && <LinearProgress />}
        {error && <div>{error}</div>}
        
        
        {entities.length > 0 && (
          <Table aria-label="group list" size="small">
            <TableHead>
              <TableRow>
                <TableCell>NAME</TableCell>
                {type === 'team' && <TableCell>OWNER</TableCell>}
                {type === 'team' && <TableCell>VS</TableCell>}
                {type === 'individual' && (
                  <TableCell>PRODUCT MANAGER</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {type === 'individual' && entities?.length > 0 &&
                entities.map((row: any, i: number) => {
                  
                  return <TableRow key={row?.metadata?.name + i}>
                    <TableCell>
                      <EntityRefLink entityRef={row}>
                        <b>{row?.metadata?.title ?? row?.metadata?.name}</b>
                      </EntityRefLink>
                    </TableCell>

                    <TableCell>
                      <>
                        {row?.spec?.owner.startsWith('user:') ? (
                          <EntityRefLink
                            entityRef={{
                              kind: 'User',
                              namespace: 'default',
                              name: row?.spec.owner.split('/')[1],
                            }}
                          />
                        ) : (
                          <EntityRefLink
                            entityRef={{
                              kind: 'Group',
                              namespace: 'default',
                              name: 'lufthansa_group',
                            }}
                          />
                        )}
                      </>
                    </TableCell>
                  </TableRow>
                }
                  
                )}
            </TableBody>
          </Table>
        )}

        {type === 'team' && (
          <>
              {contributingProductsTeam &&
              contributingProductsTeam?.length > 0 ? (
                <Table aria-label="group list" size="small">
                  <TableHead>
                    <TableCell>Product</TableCell>
                    <TableCell >Product Manager</TableCell>
                  </TableHead>
                  <TableBody>
                    {contributingProductsTeam.map((row, rowIndex) => {
                      const entity: Entity = row?.entity;
                      return (
                        <>
                          <TableRow key={`${rowIndex}-${rowIndex}`}>
                            <TableCell >
                              <EntityRefLink entityRef={entity}>
                                <b>{entity?.metadata?.title}</b>
                              </EntityRefLink>
                            </TableCell>

                            <TableCell>
                              <EntityRefLink
                                entityRef={
                                  entity?.spec?.owner
                                    ?.toString()
                                    ?.toLowerCase() as string
                                }
                              ></EntityRefLink>
                            </TableCell>
                          </TableRow>
                        </>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <EmptyState
                  missing="data"
                  title="No teams are contributing to any products."
                />
              )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
